/**
 * High-fidelity client-side downscale for technician photo uploads (Canvas + JPEG).
 * Preserves aspect ratio; skips work when both edges are already within maxDimension.
 */

function formatMb(bytes) {
  if (!bytes && bytes !== 0) return "?";
  return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
}

/**
 * @returns {Promise<{ width: number, height: number, draw: Function, dispose: Function }>}
 */
async function loadImageSource(file) {
  if (typeof createImageBitmap === "function") {
    try {
      const opts = { imageOrientation: "from-image" };
      const bitmap = await createImageBitmap(file, opts);
      return {
        width: bitmap.width,
        height: bitmap.height,
        draw(ctx, dx, dy, dw, dh) {
          ctx.drawImage(bitmap, dx, dy, dw, dh);
        },
        dispose() {
          bitmap.close?.();
        },
      };
    } catch {
      // e.g. HEIC or decode failure — try <img>
    }
  }

  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = "async";
  try {
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error("Image decode failed"));
      img.src = url;
    });
    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      draw(ctx, dx, dy, dw, dh) {
        ctx.drawImage(img, dx, dy, dw, dh);
      },
      dispose() {
        URL.revokeObjectURL(url);
      },
    };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

/**
 * Downscale raster images for faster uploads while keeping audits sharp (long edge cap + high-quality smoothing).
 * @param {File} file
 * @param {number} [maxDimension=2000]
 * @param {number} [quality=0.85] JPEG quality 0..1
 * @returns {Promise<File>} Original file if skipped or on any failure; otherwise JPEG File with same name.
 */
export async function getResizedImage(file, maxDimension = 2000, quality = 0.85) {
  try {
    if (!file || !file.type?.startsWith("image/")) {
      return file;
    }
    // Canvas rasterization of SVG is inconsistent; leave to server.
    if (file.type === "image/svg+xml") {
      return file;
    }

    const source = await loadImageSource(file);
    try {
      const { width, height, draw } = source;
      if (!width || !height) {
        return file;
      }

      if (width <= maxDimension && height <= maxDimension) {
        return file;
      }

      const scale = maxDimension / Math.max(width, height);
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return file;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      draw(ctx, 0, 0, w, h);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => {
            if (b) resolve(b);
            else reject(new Error("canvas.toBlob returned null"));
          },
          "image/jpeg",
          quality
        );
      });

      const out = new File([blob], file.name, {
        type: "image/jpeg",
        lastModified: file.lastModified,
      });

      if (process.env.NODE_ENV === "development" && out.size < file.size) {
        console.log(
          `[clientImageResize] Compressed ${file.name}: ${formatMb(file.size)} → ${formatMb(out.size)} (JPEG q=${quality}, max ${maxDimension}px)`
        );
      }

      return out;
    } finally {
      source.dispose();
    }
  } catch (err) {
    console.warn("[clientImageResize] Falling back to original:", file?.name, err);
    return file;
  }
}
